<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { isCrossDay } from '@/lib/utils'

import * as dataGrid from '@/composables/data-grid'

import { useColumns } from '@/composables/garage/worksheet-row-work-time-event-grid'
import { useWorkTimeOverlap, endEditorPreparing, createStopButton } from '@/composables/garage/work-time-events'
import { useDocumentRow } from '@/composables/document-row'

import { useGarageStore } from '@/stores/garage.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import WorkTimeOverlapWarningPopup from '@/components/shared/WorkTimeOverlapWarningPopup.vue'

import { garageApi } from '@/apis/garage.api'

const props = defineProps({
  data: Object,
  entityLock: Object,
  readOnly: {
    type: Boolean,
    default: false
  },
})

const garageStore = useGarageStore()
const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const workTimeOverlap = useWorkTimeOverlap()

const { gridRef, refreshGridAsync, focusGrid } = dataGrid.useGridRef()
const { onDocumentRowUpdated } = useDocumentRow(gridRef)

const emits = defineEmits(['row-saved'])

const saveStateId = `worksheet-row-work-time-event-grid`
const gridId = `${saveStateId}_${props.data?.id || 0}`

const i18n = getI18n()

const editors = {}
const computedDataSource = computed(() => {
  const worksheet = props.data?.id
  return dataGrid.useODataSource(
    garageApi.getAbsoluteUrl() + garageApi.workTimeEntriesUrl,
    {
      customParamsFn: () => { return { $notop: true, worksheet: worksheet } },
      avoidDuplicateDatasourceIds: true,
    }
  )
})

const selectedRows = ref([])
const formInstance = ref(null)

const columns = useColumns(editors)

const onStopClick = (e) => {
  garageApi.updateWorkTimeEntryAsync(e.id, { end: new Date().toISOString() }).then(() => {
    refreshGridAsync()
    emits('row-saved', props.data?.id)
  })
}

const rowButtons = props.entityLock.getEntityLockButtons(
  {
    visible: {
      edit: (e) => !props.readOnly || registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
      delete: (e) => !props.readOnly || registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
      view: (e) => props.readOnly && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
    }
  }
)
rowButtons.push(
  createStopButton({
    onStopClick,
    isVisible: (e) =>
      e.data.end === null &&
      !isCrossDay(e.data.start) &&
      !props.readOnly
  })
)
const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.workTimeEntry')}`
  if (i.id && i.mechanic) {
    const mechanic = webSiteStore.userDetails.find(u => u.id === i.mechanic)
    title += ` - ${mechanic?.name + ' ' + mechanic?.name_ext || ''}`
  }
  return `${title} | ${props.data?.number_mnemonic}/${props.data?.series}`
}

const onInitialized = (e) => {
  formInstance.value = e.component
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 1,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colCount: 3,
      items: [
        {
          dataField: 'start',
          editorOptions: {
            min: new Date(props.data?.date),
            max: new Date(Date.now())
          }
        },
        {
          dataField: 'end',
        },
        {
          dataField: 'duration_minutes', label: { text: `${i18n.t('common.duration')} (${i18n.t('common.minuteAlt')})` }
        },
        { dataField: 'mechanic' },
        {
          dataField: 'notes', colSpan: 2
        },
        { dataField: 'is_manual', editorType: 'dxCheckBox', editorOptions: { disabled: true }, label: { text: i18n.t('common.isManuallyInserted'), location: 'left' } },
      ]
    }
  ],
  onInitialized: onInitialized,
}, {
  allowAdding: !props.readOnly || registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
  allowUpdating: !props.readOnly || registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
  allowDeleting: !props.readOnly || registryStore.companyData.allow_work_time_entries_on_closed_worksheet,
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: props.entityLock,
  width: '50rem'
})

const onInitializedCustom = (e) => {
  if (props.readOnly && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: !props.readOnly,
      }
    })
    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    props.entityLock?.readOnlyToolbarItems(e)
  }
}

const onInitNewRow = (e) => {
  e.data.start = new Date().toISOString()
  e.data.is_manual = false
}

const onRowInserting = async(e) => {
  e.data.start = new Date(e.data.start).toISOString()
  e.data.activity_code = garageStore.workActivityEnum.WORKSHEET_PROCESSING
  e.data.worksheet = props.data?.id
}

const onSaved = () => { emits('row-saved', props.data?.id) }

const onBatchDeleteDocumentRowsAsync = async(ids) => {
  await garageApi.batchDeleteWorkTimeEntriesAsync(ids)
  emits('row-saved', props.data?.id)
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, { allowBatchDelete: true })
}

const onEditorPreparing = (e) => {
  endEditorPreparing(e, editors)
  if (props.readOnly && e.parentType === 'dataRow' && !registryStore.companyData.allow_work_time_entries_on_closed_worksheet) {
    e.editorOptions.readOnly = true
  }
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
}

const onPopupCancel = (e) => {
  workTimeOverlap.showModal.value = false
}

const onPopupSave = (e) => {
  workTimeOverlap.showModal.value = false
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  saveStateId: saveStateId,
  mode: 'row',
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onInitNewRow: onInitNewRow,
  onSelectionChanged: onSelectionChanged,
  onRowInserting: onRowInserting,
  onRowUpdated: onDocumentRowUpdated,
  onRowInserted: onDocumentRowUpdated,
  onSaved: onSaved,
  focusGrid: focusGrid,
  onDataErrorOccurred: (e) => {
    if (workTimeOverlap.buildErrorMessage(e.error?.errorDetails)) {
      workTimeOverlap.showModal.value = true
      return
    }

    dataGrid.useDefaultErrorHandler(e)
  },
  disabledFocusOnContentReady: true,
  splittedGridDetail: true,
  rowDragging: {
    allowReordering: false,
  },
})

</script>
<template>
  <div class="row-grid-ctn">
    <dx-data-grid
      ref="gridRef"
      :key="gridId"
      v-bind="gridOptions"
      :data-source="computedDataSource"
    >
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :master-entity-id="props.data?.id"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
      <template #customToolbarDeleteButtonTemplate>
        <custom-toolbar-delete-button-template
          :master-entity-id="props.data?.id"
          :selected-rows-id="selectedRows?.map(i => i.id) || []"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :on-delete="onBatchDeleteDocumentRowsAsync"
          :disabled="props.readOnly || selectedRows?.length === 0"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Worksheet'"
          :master-entity-id="props.data?.id"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
    </dx-data-grid>
    <work-time-overlap-warning-popup
      v-if="workTimeOverlap.showModal.value"
      v-model:visible="workTimeOverlap.showModal.value"
      :options="{
        newWorkTimeEntry: workTimeOverlap.newWorkTimeEntry.value,
        mechanic: workTimeOverlap.warningOverlappedMechanic.value,
        workTimeEntries: workTimeOverlap.warningOverlappedWorkTimeEntries.value,
        worksheets: workTimeOverlap.warningOverlappedWorksheets.value,
        title: $t('error.validation.work_time_entry_overlap') }"
      @cancel="onPopupCancel"
      @save="onPopupSave"
    />
  </div>
</template>

<style scoped lang="scss">
:deep(.icon-red) {
  color: $negative !important;
}
</style>
